/**
 * Luxury motion layer for the homepage.
 *
 * - Scroll reveal: sections (and, inside hero sections, each text/button
 *   block) fade + rise into place once, the first time they're seen.
 * - Hero scroll effects: each hero declares a `data-scroll-effect`
 *   (parallax / zoom_in / zoom_out) and its media is scaled/translated as
 *   the section scrolls through the viewport, so different heroes can move
 *   differently instead of all sharing identical parallax.
 *
 * No section/theme markup is required for this to work — it targets the
 * standard Horizon section wrappers and hero markup directly. Respects
 * prefers-reduced-motion and is a no-op if IntersectionObserver is
 * unavailable.
 */
(function () {
  'use strict';

  var prefersReduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;

  var revealInitialized = false;
  var parallaxInitialized = false;

  function staggerChildren(container) {
    if (!container) return;
    Array.prototype.forEach.call(container.children, function (el, i) {
      el.style.setProperty('--lux-delay', Math.min(i * 90, 360) + 'ms');
    });
  }

  function setupReveal() {
    var main = document.getElementById('MainContent');
    if (!main) return;

    var sections = Array.prototype.slice.call(
      main.querySelectorAll(':scope > .shopify-section')
    );
    if (!sections.length) return;

    sections.forEach(function (section) {
      staggerChildren(section.querySelector('.hero__content-wrapper'));
      section.classList.add('lux-reveal');
    });

    if (prefersReduced || typeof IntersectionObserver === 'undefined') {
      sections.forEach(function (s) {
        s.classList.add('lux-reveal-in');
      });
      return;
    }

    // First section: cinematic entrance on load rather than on scroll,
    // since it's already in view.
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        sections[0].classList.add('lux-reveal-in');
      });
    });

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('lux-reveal-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    sections.slice(1).forEach(function (section) {
      io.observe(section);
    });

    revealInitialized = true;
  }

  function setupScrollEffects() {
    if (prefersReduced) return;

    var heroes = Array.prototype.slice
      .call(document.querySelectorAll('.hero[data-scroll-effect]'))
      .filter(function (hero) {
        return hero.dataset.scrollEffect && hero.dataset.scrollEffect !== 'none';
      });
    if (!heroes.length) return;

    var mobile = isMobile;
    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;

      heroes.forEach(function (hero) {
        var effect = hero.dataset.scrollEffect;
        var rect = hero.getBoundingClientRect();
        var travel = Math.max(vh + rect.height, 1);
        var progress = (vh - rect.top) / travel;
        if (progress < 0) progress = 0;
        if (progress > 1) progress = 1;

        var translateY = 0;
        var scale = 1;

        if (effect === 'parallax') {
          // Scale overscan is calibrated to exactly match the translate
          // range, so no edge of the media is ever exposed regardless of
          // speed.
          var speed = mobile ? 6 : 12;
          scale = 1 + speed / 100;
          translateY = speed / 2 - progress * speed;
        } else if (effect === 'zoom_in' || effect === 'zoom_out') {
          var zoomScale = mobile ? 1.08 : 1.15;
          var delta = zoomScale - 1;
          scale = effect === 'zoom_in' ? 1 + progress * delta : zoomScale - progress * delta;
        }

        hero.style.setProperty('--scroll-media-scale', scale.toFixed(4));
        hero.style.setProperty('--scroll-media-translate-y', translateY.toFixed(3) + '%');
      });
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    parallaxInitialized = true;
  }

  function init() {
    if (!revealInitialized) setupReveal();
    if (!parallaxInitialized) setupScrollEffects();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // Re-run for content the theme editor / section-rendering API swaps in
  // after first load. Guards above stop this from doing redundant work
  // for the parts that already initialized.
  document.addEventListener('shopify:section:load', function () {
    revealInitialized = false;
    setupReveal();
  });
})();
